import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Card, Badge } from "../components/ui";

const MOTIVO_LABELS = {
  fornitore_non_riconosciuto: "Fornitore non riconosciuto",
  errore_parsing: "Errore parsing",
  confidence_bassa: "Bassa confidence",
  errore_upload: "Errore upload",
};

const MOTIVO_VARIANTS = {
  fornitore_non_riconosciuto: "warning",
  errore_parsing: "danger",
  confidence_bassa: "warning",
  errore_upload: "danger",
};

const STATO_LABELS = {
  pendente: "Da rivedere",
  risolto: "Risolto",
  ignorato: "Ignorato",
};

const STATO_VARIANTS = {
  pendente: "warning",
  risolto: "success",
  ignorato: "default",
};

function ReviewItem({ item, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  const date = item.receivedAt
    ? new Date(item.receivedAt).toLocaleString("it-IT")
    : "—";
  const createdAt = item.createdAt
    ? new Date(item.createdAt).toLocaleString("it-IT")
    : "—";

  const handleAction = async (stato) => {
    setLoading(true);
    await onUpdate(item.id, { stato });
    setLoading(false);
  };

  const isPendente = item.stato === "pendente";

  return (
    <Card
      className={`p-4 border-l-4 ${
        item.stato === "pendente"
          ? item.motivo === "confidence_bassa"
            ? "border-l-yellow-400"
            : "border-l-red-400"
          : item.stato === "risolto"
            ? "border-l-green-400"
            : "border-l-gray-300"
      }`}
    >
      <div
        className="flex items-start justify-between gap-3 cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={MOTIVO_VARIANTS[item.motivo] || "default"}>
              {MOTIVO_LABELS[item.motivo] || item.motivo}
            </Badge>
            <Badge variant={STATO_VARIANTS[item.stato] || "default"}>
              {STATO_LABELS[item.stato] || item.stato}
            </Badge>
            {item.confidence != null && (
              <span className="text-xs text-gray-500">
                confidence {item.confidence}%
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="font-medium text-gray-800 truncate max-w-xs">
              {item.pdfName || "PDF sconosciuto"}
            </span>
            <span className="text-gray-400">·</span>
            <span className="text-gray-500 truncate max-w-xs">{item.from}</span>
          </div>
          <span className="text-xs text-gray-400">
            Email ricevuta: {date} · Rilevato: {createdAt}
          </span>
        </div>
        <span className="text-gray-400 text-sm shrink-0 mt-1">
          {expanded ? "▲" : "▼"}
        </span>
      </div>

      {expanded && (
        <div className="mt-3 border-t pt-3 space-y-2">
          {item.emailSubject && (
            <p className="text-sm text-gray-600">
              <span className="font-medium">Oggetto email:</span>{" "}
              {item.emailSubject}
            </p>
          )}
          {item.consumoId && (
            <p className="text-sm text-gray-600">
              <span className="font-medium">Consumo creato:</span>{" "}
              <Link
                to={`/consumi-utenze/${item.consumoId}`}
                className="text-blue-600 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                Apri consumo →
              </Link>
            </p>
          )}
          {item.warnings && item.warnings.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Warning:</p>
              <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                {item.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}
          {item.note && (
            <p className="text-sm text-gray-600">
              <span className="font-medium">Note:</span> {item.note}
            </p>
          )}
          {item.resolvedBy && (
            <p className="text-xs text-gray-400">
              Chiuso da {item.resolvedBy} il{" "}
              {item.resolvedAt
                ? new Date(item.resolvedAt).toLocaleString("it-IT")
                : "—"}
            </p>
          )}

          {isPendente && (
            <div
              className="flex gap-2 pt-1"
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleAction("risolto")}
                disabled={loading}
              >
                Segna risolto
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleAction("ignorato")}
                disabled={loading}
              >
                Ignora
              </Button>
            </div>
          )}
          {!isPendente && (
            <div onClick={(e) => e.stopPropagation()}>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleAction("pendente")}
                disabled={loading}
              >
                Riapri
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function BollettaReviewPage() {
  const { apiRequest } = useAuth();
  const { showToast } = useToast();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ pendente: 0, risolto: 0, ignorato: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pendente");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRequest(
        `/bollette-review${filter !== "tutti" ? `?stato=${filter}` : ""}`,
      );
      setItems(data.value || []);
      setStats(data.stats || { pendente: 0, risolto: 0, ignorato: 0 });
    } catch {
      showToast("Errore caricamento coda revisione", "error");
    } finally {
      setLoading(false);
    }
  }, [apiRequest, showToast, filter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpdate = async (id, updates) => {
    try {
      const updated = await apiRequest(`/bollette-review/${id}`, {
        method: "PATCH",
        body: JSON.stringify(updates),
      });
      setItems((prev) =>
        filter === "tutti"
          ? prev.map((i) => (i.id === id ? updated : i))
          : prev.filter((i) => i.id !== id),
      );
      if (updates.stato === "risolto" || updates.stato === "ignorato") {
        setStats((s) => ({
          ...s,
          pendente: Math.max(0, s.pendente - 1),
          [updates.stato]: s[updates.stato] + 1,
        }));
      }
      showToast("Aggiornato", "success");
    } catch {
      showToast("Errore aggiornamento", "error");
    }
  };

  const FILTERS = [
    { key: "pendente", label: `Da rivedere (${stats.pendente})` },
    { key: "tutti", label: "Tutti" },
    { key: "risolto", label: "Risolti" },
    { key: "ignorato", label: "Ignorati" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Bollette da Rivedere
          </h1>
          <p className="text-gray-500 mt-1">
            PDF non processati automaticamente che richiedono intervento manuale
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          {loading ? "Caricamento..." : "Aggiorna"}
        </Button>
      </div>

      {stats.pendente > 0 && (
        <Card className="p-4 bg-amber-50 border-amber-200 flex items-center gap-3">
          <span className="text-2xl">⚠️</span>
          <div>
            <p className="font-medium text-amber-800">
              {stats.pendente}{" "}
              {stats.pendente === 1
                ? "bolletta richiede"
                : "bollette richiedono"}{" "}
              revisione
            </p>
            <p className="text-sm text-amber-600">
              Questi PDF non sono stati processati correttamente in modo
              automatico.
            </p>
          </div>
        </Card>
      )}

      <div className="flex gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              filter === f.key
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner w-8 h-8" />
        </div>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-gray-500">
          {filter === "pendente"
            ? "Nessuna bolletta in attesa di revisione"
            : "Nessun elemento"}
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <ReviewItem key={item.id} item={item} onUpdate={handleUpdate} />
          ))}
        </div>
      )}
    </div>
  );
}
